import type { AssetProvenance, ProvenanceStatus } from "../types";

type RecordValue = Record<string, unknown>;

function asRecord(value: unknown): RecordValue {
    return typeof value === "object" && value !== null && !Array.isArray(value) ? (value as RecordValue) : {};
}

function field(record: RecordValue, snakeKey: string, camelKey: string): unknown {
    if (snakeKey in record) return record[snakeKey];
    return record[camelKey];
}

function stringField(record: RecordValue, snakeKey: string, camelKey: string): string | null {
    const value = field(record, snakeKey, camelKey);
    return typeof value === "string" ? value : null;
}

function requiredStringField(record: RecordValue, snakeKey: string, camelKey: string): string {
    return stringField(record, snakeKey, camelKey) ?? "";
}

function booleanField(record: RecordValue, snakeKey: string, camelKey: string): boolean {
    return field(record, snakeKey, camelKey) === true;
}

function numberField(record: RecordValue, snakeKey: string, camelKey: string): number | null {
    const value = field(record, snakeKey, camelKey);
    return typeof value === "number" ? value : null;
}

function objectField(record: RecordValue, snakeKey: string, camelKey: string): RecordValue | null {
    const value = field(record, snakeKey, camelKey);
    return typeof value === "object" && value !== null && !Array.isArray(value)
        ? (value as RecordValue)
        : null;
}

function arrayField(record: RecordValue, snakeKey: string, camelKey: string): unknown[] {
    const value = field(record, snakeKey, camelKey);
    return Array.isArray(value) ? value : [];
}

/** Translate the backend state columns into the small set of states the UI can explain. */
export function mapProvenanceStatus(record: RecordValue): ProvenanceStatus {
    if (booleanField(record, "quarantined", "quarantined")) return "quarantined";
    if (booleanField(record, "unavailable", "unavailable")) return "unavailable";

    switch (stringField(record, "import_completeness", "importCompleteness")) {
        case "in_progress":
        case "partial":
        case "incomplete":
            return "pending";
        case "failed":
            return "failed";
        case "complete":
        case "not_applicable":
            return "ready";
        default:
            return "unavailable";
    }
}

/** Map either an untouched snake_case response or the shared client's camelCase response. */
export function mapAssetProvenance(payload: unknown): AssetProvenance {
    const record = asRecord(payload);
    const settingsJson = objectField(record, "settings_json", "settingsJson");
    const fullRequestJson = objectField(record, "full_request_json", "fullRequestJson");

    return {
        id: requiredStringField(record, "id", "id"),
        artifactId: stringField(record, "artifact_id", "artifactId"),
        projectMediaId: stringField(record, "project_media_id", "projectMediaId"),
        chatAttachmentId: stringField(record, "chat_attachment_id", "chatAttachmentId"),
        generationJobId: stringField(record, "generation_job_id", "generationJobId"),
        workspaceId: stringField(record, "workspace_id", "workspaceId"),
        projectId: stringField(record, "project_id", "projectId"),
        actorId: stringField(record, "actor_id", "actorId"),
        origin: requiredStringField(record, "origin", "origin"),
        platform: stringField(record, "platform", "platform"),
        occurredAt: stringField(record, "occurred_at", "occurredAt"),
        createdAt: requiredStringField(record, "created_at", "createdAt"),
        sha256: stringField(record, "sha256", "sha256"),
        bytes: numberField(record, "bytes", "bytes"),
        mediaType: requiredStringField(record, "media_type", "mediaType"),
        mimeType: stringField(record, "mime_type", "mimeType"),
        sourceUrl: stringField(record, "source_url", "sourceUrl"),
        storageUrl: stringField(record, "storage_url", "storageUrl"),
        prompt: stringField(record, "prompt", "prompt"),
        instruction: stringField(record, "instruction", "instruction"),
        model: stringField(record, "model", "model"),
        settingsJson,
        fullRequestJson,
        references: arrayField(record, "references_json", "referencesJson"),
        lineage: objectField(record, "lineage_json", "lineageJson"),
        importCompleteness: requiredStringField(record, "import_completeness", "importCompleteness"),
        quarantined: booleanField(record, "quarantined", "quarantined"),
        quarantineReason: stringField(record, "quarantine_reason", "quarantineReason"),
        unavailable: booleanField(record, "unavailable", "unavailable"),
        existingC2pa: booleanField(record, "existing_c2pa", "existingC2pa"),
        manifestPointer: stringField(record, "manifest_pointer", "manifestPointer"),
        manifestStatus: requiredStringField(record, "manifest_status", "manifestStatus"),
        status: mapProvenanceStatus(record),
        rawJson: fullRequestJson,
    };
}
